import React, { useState, useEffect } from 'react';

interface PrepareEnrichStepProps {
  data?: any;
  profileData?: any; // Profile data to check marital status and get contact info
  onComplete: (data: any) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const PrepareEnrichStep: React.FC<PrepareEnrichStepProps> = ({ 
  data, 
  profileData,
  onComplete, 
  onNext, 
  onPrevious 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if this step has already been completed
    if (data?.requested) {
      setIsRequested(true);
    }
  }, [data]);

  const handleRequestCounseling = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const requestData = {
        candidateName: `${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim(),
        candidateEmail: profileData?.email || '',
        candidatePhone: profileData?.phone || '',
        wifeName: `${profileData?.wifeFirstName || ''} ${profileData?.wifeLastName || ''}`.trim(),
        wifeEmail: profileData?.wifeEmail || '',
        wifePhone: profileData?.wifePhone || '',
        requested: true,
        requestedAt: new Date().toISOString(),
      };

      await onComplete(requestData);
      setIsRequested(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit request. Please try again.');
      console.error('Error requesting counseling:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if candidate is married
  const isMarried = profileData?.maritalStatus?.toLowerCase() === 'married';

  const handleSkipForSingle = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await onComplete({
        skipped: true,
        skippedAt: new Date().toISOString(),
        requested: false,
      });
      onNext();
    } catch (err: any) {
      setError(err.message || 'Failed to skip this step. Please try again.');
      console.error('Error skipping Prepare/Enrich step:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isMarried) {
    return (
      <div className="p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-blue-800">
                This Step is Optional for Single Candidates
              </h3>
              <p className="mt-2 text-sm text-blue-700">
                The Prepare/Enrich counseling process is designed for married couples. 
                Since you indicated you are not married, you can skip this step.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-between">
          <button
            type="button"
            onClick={onPrevious}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={handleSkipForSingle}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Skip This Step'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Prepare/Enrich Counseling</h2>
        
        <div className="prose max-w-none">
          <p className="text-gray-700 mb-4">
            As part of the church planting preparation process, we require all married couples to complete 
            the Prepare/Enrich assessment and counseling process. This valuable tool will help strengthen 
            your marriage as you prepare for the unique challenges and opportunities of church planting ministry.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">What to Expect:</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>A trained counselor will contact you within 3-5 business days to schedule your assessment</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>You and your spouse will each complete an online assessment (approximately 30-45 minutes)</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>The entire process typically takes 2-3 weeks to complete</span>
              </li>
            </ul>
          </div>

          {isRequested ? (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-green-800">Request Submitted Successfully!</h3>
                    <p className="mt-2 text-sm text-green-700">
                      Your request for Prepare/Enrich counseling has been submitted. A counselor will contact you 
                      at the email address and phone number you provided in your profile. Please allow 3-5 business 
                      days for initial contact.
                    </p>
                    {data?.requestedAt && (
                      <p className="mt-2 text-xs text-green-600">
                        Requested on: {new Date(data.requestedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Admin Status */}
              {profileData?.prepareEnrichStatus && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Status</h3>
                  <div className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Counseling Status</dt>
                      <dd className="mt-1">
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                          profileData.prepareEnrichStatus === 'complete' ? 'bg-green-100 text-green-800' :
                          profileData.prepareEnrichStatus === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                          profileData.prepareEnrichStatus === 'started' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {profileData.prepareEnrichStatus === 'not_started' && 'Not Started'}
                          {profileData.prepareEnrichStatus === 'started' && 'Started - Awaiting Contact'}
                          {profileData.prepareEnrichStatus === 'scheduled' && 'Scheduled'}
                          {profileData.prepareEnrichStatus === 'complete' && 'Complete ✓'}
                        </span>
                      </dd>
                    </div>
                    {profileData.prepareEnrichStatusUpdatedAt && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(profileData.prepareEnrichStatusUpdatedAt).toLocaleString()}
                        </dd>
                      </div>
                    )}
                    {profileData.prepareEnrichNotes && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Notes from Counselor</dt>
                        <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                          {profileData.prepareEnrichNotes}
                        </dd>
                      </div>
                    )}
                  </div>
                  
                  {profileData.prepareEnrichStatus !== 'complete' && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Your request is being processed. You'll receive a call or email from a counselor soon.
                      </p>
                    </div>
                  )}
                  
                  {profileData.prepareEnrichStatus === 'complete' && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-800">
                        <svg className="inline h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        You have completed the Prepare/Enrich counseling process!
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Contact Information:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-gray-700">Candidate:</p>
                  <p className="text-gray-600">{profileData?.firstName} {profileData?.lastName}</p>
                  <p className="text-gray-600">{profileData?.email}</p>
                  <p className="text-gray-600">{profileData?.phone}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">Spouse:</p>
                  <p className="text-gray-600">{profileData?.wifeFirstName} {profileData?.wifeLastName}</p>
                  <p className="text-gray-600">{profileData?.wifeEmail}</p>
                  <p className="text-gray-600">{profileData?.wifePhone}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                This information will be shared with the counselor when you submit your request.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onPrevious}
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          Previous
        </button>
        <div className="flex space-x-3">
          {isRequested && (
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Continue
            </button>
          )}
          {!isRequested && (
            <button
              type="button"
              onClick={handleRequestCounseling}
              disabled={isLoading}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting Request...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Request Prepare and Enrich Assessment
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrepareEnrichStep;



